import {
  DeleteOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  MinusOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import {
  Badge,
  Button,
  DatePicker,
  Divider,
  Empty,
  Image,
  Input,
  InputNumber,
  List,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import moment from 'moment-timezone';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RED1 } from '../../constants/color';
import { SHIPPING_CODE } from '../../constants/orderConstants';
import { dashboardRoute } from '../../constants/pathname';
import { FALLBACK_IMG } from '../../constants/styles';
import {
  CreditLimit,
  OrderEnum,
  PaymentMethod,
  ShippingData,
  UserListData,
} from '../../types';
import { alertMessage } from '../../utils/alertMessage';
import getDashboardStyle from '../../utils/getDashboardStyle';
import { onOrderSubmit } from '../../utils/orderHelper';
import UserAddressDropdown from '../users/common/UserAddressDropdown';
import UserCreditLimit from '../users/userDetail/UserCreditLimit';

/**
 * Fee lines of the order/preview `orderAmount` response, in the same order and
 * with the same +/- signs as the order wizard's TotalFeeForm. Any line that
 * comes back non-zero is shown; `orderAmount` itself is the grand total and is
 * rendered separately.
 */
const FEE_ROWS: { key: string; label: string; prefix: string }[] = [
  { key: 'goodsAmount', label: 'order.orderDetail.goodsAmount', prefix: '' },
  { key: 'gstFee', label: 'order.orderDetail.gst', prefix: '+' },
  { key: 'pstFee', label: 'order.orderDetail.pst', prefix: '+' },
  { key: 'depositFee', label: 'order.orderDetail.depositFee', prefix: '+' },
  { key: 'recycleFee', label: 'order.orderDetail.recycleFee', prefix: '+' },
  { key: 'shippingFee', label: 'order.orderDetail.shippingFee', prefix: '+' },
  { key: 'payFee', label: 'order.orderDetail.payFee', prefix: '+' },
  { key: 'tipsFee', label: 'order.orderDetail.tipsFee', prefix: '+' },
  { key: 'packFee', label: 'order.orderDetail.packFee', prefix: '+' },
  {
    key: 'discountAmount',
    label: 'order.orderDetail.discountAmount',
    prefix: '-',
  },
  { key: 'totalAmount', label: 'order.orderDetail.totalAmount', prefix: '' },
  { key: 'bonusAmount', label: 'order.orderDetail.bonusAmount', prefix: '-' },
  {
    key: 'couponsAmount',
    label: 'order.orderDetail.couponsAmount',
    prefix: '-',
  },
  {
    key: 'integralAmount',
    label: 'order.orderDetail.integralAmount',
    prefix: '-',
  },
  {
    key: 'valueCardAmount',
    label: 'order.orderDetail.valueCardAmount',
    prefix: '-',
  },
  {
    key: 'surplusAmount',
    label: 'order.orderDetail.surplusAmount',
    prefix: '-',
  },
];

type PosCartProps = {
  formData: { [key: string]: any };
  setFormData: React.Dispatch<React.SetStateAction<{ [key: string]: any }>>;
  enums?: OrderEnum;
  shippingList: ShippingData[];
  paymentMethods: PaymentMethod[];
  selectedUser?: UserListData;
  orderAmount: { [key: string]: any };
  addressLoaded: boolean;
  recalcGoods: (orderGoodsList: any[]) => void;
  onSubmitted: () => void;
};

/**
 * Right pane of the POS terminal: cart line items (scrollable), fulfillment
 * (shipping/pickup + time), customer address, payment method and the confirm
 * button. Order creation reuses onOrderSubmit (POST order/add); qty/remove
 * edits reuse the parent's recalcGoods (POST order/preview_goods).
 */
const PosCart = ({
  formData,
  setFormData,
  enums,
  shippingList,
  paymentMethods,
  selectedUser,
  orderAmount,
  addressLoaded,
  recalcGoods,
  onSubmitted,
}: PosCartProps) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [editAddress, setEditAddress] = useState(false);
  const [showRemarks, setShowRemarks] = useState(false);
  /** Cart line whose name/unit/price panel is expanded, if any */
  const [editingKey, setEditingKey] = useState<string>();
  // Wholesale credit terms; self-fetched by UserCreditLimit on customer change
  const [creditLimit, setCreditLimit] = useState<CreditLimit>();
  const [creditLimitLoading, setCreditLimitLoading] = useState(false);

  const openUserDetail = () =>
    selectedUser &&
    window.open(
      `${dashboardRoute.users.detail}?user_id=${selectedUser.userId}`,
      '_blank'
    );

  const orderGoodsList: any[] = formData.orderGoodsList || [];
  const isDelivery = formData.shippingCode === SHIPPING_CODE['flat'];
  const userBalance = Number(selectedUser?.userMoney) || 0;
  // Pickup/delivery date is only mandatory for wholesale (see ShippingPaymentForm)
  const dateRequired = getDashboardStyle().isWholeSaleAppType;
  const remarkCount = [
    formData.buyerRemark,
    formData.sellerRemark,
    formData.deliveryRemark,
  ].filter(Boolean).length;

  const rowKey = (item: any) => `${item.goodsId}_${item.goodsAttrId || ''}`;

  /**
   * Edits one field of a cart line, mirroring OrderGoodsForm.updateGoodsList:
   * name/unit are local-only, while anything affecting the price is re-priced
   * server-side via order/preview_goods.
   */
  const updateItem = (
    index: number,
    key: string,
    value: string | number | null,
    recalc = false
  ) => {
    const newList = orderGoodsList.slice();
    newList[index] = { ...newList[index], [key]: value };
    if (recalc) {
      recalcGoods(newList);
    } else {
      setFormData((prev) => ({ ...prev, orderGoodsList: newList }));
    }
  };

  const changeQty = (index: number, delta: number) => {
    const next = Number(orderGoodsList[index].goodsNumber) + delta;
    if (next < 1) return;
    updateItem(index, 'goodsNumber', next, true);
  };

  const removeItem = (index: number) => {
    const newList = orderGoodsList.filter((_, i) => i !== index);
    recalcGoods(newList);
  };

  /** Re-price the current list (used on blur of the numeric fields) */
  const reprice = () => recalcGoods(orderGoodsList);

  const setField = (key: string, value: any) =>
    setFormData((prev) => ({ ...prev, [key]: value }));

  // Labeled text input bound to an address field of formData
  const addressField = (key: string, label: string) => (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      <Input
        value={formData[key]}
        placeholder={label}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  // Labeled multiline remark bound to formData (buyer/seller/delivery)
  const remarkField = (key: string, label: string) => (
    <div>
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {label}
      </Typography.Text>
      <Input.TextArea
        autoSize={{ minRows: 1, maxRows: 3 }}
        value={formData[key]}
        placeholder={label}
        onChange={(e) => setField(key, e.target.value)}
      />
    </div>
  );

  const applyAddress = (address: any) =>
    setFormData((prev) => ({
      ...prev,
      ...address,
      addressType: address.addressType
        ? address.addressType.code
        : prev.addressType,
      latitude: Number(address.latitude),
      longitude: Number(address.longitude),
    }));

  const onShippingChange = (value: number, option: any) => {
    const code = option && option.key;
    setFormData((prev) => {
      const pickup = code !== SHIPPING_CODE['flat'];
      return {
        ...prev,
        shippingId: value,
        shippingCode: code,
        shippingName: option && option.children,
        // Pickup defaults to today; clear interval when switching to pickup
        expectShippingTime:
          pickup && !prev.expectShippingTime ? moment() : prev.expectShippingTime,
        expectShippingTimeInterval: pickup
          ? undefined
          : prev.expectShippingTimeInterval,
      };
    });
  };

  const hasAddress = !!(formData.consignee && formData.address);

  const onConfirm = () => {
    if (!orderGoodsList.length) return;
    if (!formData.payId) {
      alertMessage('warning', t('order.add/editOrder.paymentMethod'));
      return;
    }
    // Pickup / delivery date is mandatory for wholesale
    if (dateRequired && !formData.expectShippingTime) {
      alertMessage(
        'warning',
        isDelivery
          ? t('order.orderDetail.expectedShippingTime')
          : t('order.orderDetail.expectedPickupTime')
      );
      return;
    }
    if (!hasAddress) {
      alertMessage('warning', t('pos.missingAddress'));
      setEditAddress(true);
      return;
    }
    onOrderSubmit(formData, t, setSubmitting, onSubmitted);
  };

  const feeRow = (label: string, value: string | number, prefix: string) => (
    <div
      key={label}
      style={{ display: 'flex', justifyContent: 'space-between' }}
    >
      <Typography.Text type="secondary">{label}</Typography.Text>
      <Typography.Text>{`${prefix}$${value}`}</Typography.Text>
    </div>
  );

  return (
    <>
      {/* Header: full customer info so the picked user is identifiable */}
      <div style={{ padding: '12px 12px 8px', borderBottom: '1px solid #f0f0f0' }}>
        <Typography.Title level={5} style={{ margin: 0 }}>
          {t('pos.cart')}
        </Typography.Title>
        {selectedUser ? (
          <Space size={4} wrap style={{ fontSize: 13 }}>
            <Typography.Link strong onClick={openUserDetail}>
              {selectedUser.userName}
              {selectedUser.nickName ? ` (${selectedUser.nickName})` : ''}
            </Typography.Link>
            {selectedUser.mobilePhone && (
              <Typography.Text type="secondary">
                {selectedUser.mobilePhone}
              </Typography.Text>
            )}
            {selectedUser.userRank &&
              selectedUser.userRank.rankId > 0 &&
              selectedUser.userRank.rankName && (
                <Tag color="orange" style={{ marginInlineEnd: 0 }}>
                  {selectedUser.userRank.rankName}
                </Tag>
              )}
            {selectedUser.member &&
              selectedUser.member.rankId > 0 &&
              selectedUser.member.rankName && (
                <Tag color="purple" style={{ marginInlineEnd: 0 }}>
                  {selectedUser.member.rankName}
                </Tag>
              )}
            {selectedUser.userMoney !== undefined && (
              <Typography.Text type="success">
                <DollarOutlined /> {selectedUser.userMoney}
              </Typography.Text>
            )}
          </Space>
        ) : (
          <Typography.Text type="secondary">
            {t('pos.walkInCustomer')}
          </Typography.Text>
        )}
        {/* Wholesale credit-terms reminder (self-hides otherwise) */}
        <UserCreditLimit
          compactUI
          userId={formData.userId}
          userName={selectedUser?.userName}
          sellerId={formData.sellerId}
          creditLimit={creditLimit}
          setCreditLimit={setCreditLimit}
          creditLimitLoading={creditLimitLoading}
          setCreditLimitLoading={setCreditLimitLoading}
          showDetail
        />
      </div>

      {/* Cart items (scrollable) */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: '0 12px' }}>
        {!orderGoodsList.length ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={t('pos.emptyCart')}
            style={{ marginTop: 40 }}
          />
        ) : (
          <List
            size="small"
            dataSource={orderGoodsList}
            rowKey={rowKey}
            renderItem={(item, index) => (
              <List.Item style={{ padding: '10px 0' }}>
                <div style={{ width: '100%' }}>
                  {/* Read-only summary: only qty is edited on most sales */}
                  <div
                    style={{ display: 'flex', gap: 12, alignItems: 'center' }}
                  >
                    <Image
                      src={item.thumbPicPath || item.thumbPic}
                      fallback={FALLBACK_IMG}
                      preview={false}
                      width={56}
                      height={56}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <Typography.Text
                        ellipsis={{ tooltip: item.goodsName }}
                        style={{ display: 'block' }}
                      >
                        {item.goodsName}
                      </Typography.Text>
                      <Typography.Text
                        type="secondary"
                        style={{ fontSize: 12 }}
                      >
                        {item.goodsUnit}
                      </Typography.Text>
                      {item.goodsAttrName && (
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12, display: 'block' }}
                          ellipsis={{ tooltip: item.goodsAttrName }}
                        >
                          {item.goodsAttrName}
                        </Typography.Text>
                      )}
                      {(item.isWeightGoods ||
                        Number(item.goodsWeight) > 0) && (
                        <Typography.Text
                          type={
                            item.isWeightGoods &&
                            !(Number(item.goodsWeight) > 0)
                              ? 'danger'
                              : 'secondary'
                          }
                          style={{ fontSize: 12, display: 'block' }}
                        >
                          {`${t('goods.goodsListColumns.goodsWeight')}: ${
                            item.goodsWeight || 0
                          }`}
                        </Typography.Text>
                      )}
                    </div>
                    <div style={{ width: 72, textAlign: 'center' }}>
                      <Typography.Text
                        type="danger"
                        strong
                        style={{ fontSize: 16 }}
                      >
                        {`$${item.goodsPrice}`}
                      </Typography.Text>
                    </div>
                    <Space direction="vertical" align="center" size={4}>
                      <Space size={6}>
                        <Button
                          size="large"
                          shape="circle"
                          icon={<MinusOutlined />}
                          onClick={() => changeQty(index, -1)}
                        />
                        <InputNumber
                          style={{ width: 56 }}
                          type="number"
                          min={1}
                          precision={0}
                          value={item.goodsNumber}
                          onChange={(value: number | null) =>
                            updateItem(index, 'goodsNumber', value)
                          }
                          onBlur={() => {
                            if (!item.goodsNumber || item.goodsNumber < 1) {
                              updateItem(index, 'goodsNumber', 1, true);
                            } else {
                              reprice();
                            }
                          }}
                        />
                        <Button
                          size="large"
                          shape="circle"
                          icon={<PlusOutlined />}
                          onClick={() => changeQty(index, 1)}
                        />
                        <Button
                          size="large"
                          type="text"
                          danger
                          icon={<DeleteOutlined style={{ fontSize: 20 }} />}
                          onClick={() => removeItem(index)}
                        />
                      </Space>
                      <Space size={4}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {`${t('order.orderDetail.subTotal')}: $${
                            item.totalPrice || '0.00'
                          }`}
                        </Typography.Text>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0 }}
                          icon={<EditOutlined />}
                          onClick={() =>
                            setEditingKey((prev) =>
                              prev === rowKey(item) ? undefined : rowKey(item)
                            )
                          }
                        />
                      </Space>
                    </Space>
                  </div>

                  {/* Occasional edits, hidden behind the pencil */}
                  {editingKey === rowKey(item) && (
                    <div
                      style={{
                        marginTop: 8,
                        padding: 8,
                        background: '#fafafa',
                        borderRadius: 4,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 8,
                      }}
                    >
                      <div style={{ flex: '1 1 100%' }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {t('goods.goodsListColumns.goodsName')}
                        </Typography.Text>
                        <Input
                          value={item.goodsName}
                          onChange={(e) =>
                            updateItem(index, 'goodsName', e.target.value)
                          }
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {t('goods.goodsListColumns.goodsUnit')}
                        </Typography.Text>
                        <Input
                          value={item.goodsUnit}
                          onChange={(e) =>
                            updateItem(index, 'goodsUnit', e.target.value)
                          }
                        />
                      </div>
                      <div style={{ flex: 1 }}>
                        <Typography.Text
                          type="secondary"
                          style={{ fontSize: 12 }}
                        >
                          {t('goods.goodsListColumns.shopPrice')}
                        </Typography.Text>
                        <InputNumber
                          style={{ width: '100%' }}
                          type="number"
                          min={0}
                          precision={2}
                          value={item.goodsPrice}
                          readOnly={getDashboardStyle().isSalesForceAppType}
                          bordered={!getDashboardStyle().isSalesForceAppType}
                          onChange={(value: number | null) =>
                            updateItem(index, 'goodsPrice', value)
                          }
                          onBlur={reprice}
                        />
                      </div>
                      {item.isWeightGoods && (
                        <div style={{ flex: '1 1 100%' }}>
                          <Typography.Text
                            type="secondary"
                            style={{ fontSize: 12 }}
                          >
                            {t('goods.goodsListColumns.goodsWeight')}
                          </Typography.Text>
                          <InputNumber
                            style={{ width: '100%' }}
                            type="number"
                            min={0}
                            value={
                              item.goodsWeight !== undefined &&
                              item.goodsWeight !== ''
                                ? parseFloat(item.goodsWeight)
                                : undefined
                            }
                            onChange={(value: number | null) =>
                              updateItem(index, 'goodsWeight', value)
                            }
                            onBlur={() => {
                              if (Number(item.goodsWeight) <= 0) {
                                alertMessage(
                                  'warning',
                                  t('goods.alerts.missingWeightWarning')
                                );
                              } else {
                                reprice();
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </List.Item>
            )}
          />
        )}
      </div>

      {/* Fulfillment + address + checkout (fixed, scrolls if tall) */}
      <div
        style={{
          borderTop: '1px solid #f0f0f0',
          padding: 12,
          background: '#fafafa',
          maxHeight: '55%',
          overflow: 'auto',
        }}
      >
        {/* Shipping method + required pickup/delivery date */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <Select
            style={{ flex: 1 }}
            placeholder={t('order.orderDetail.shippingName')}
            value={formData.shippingId}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onChange={onShippingChange}
          >
            {shippingList.map((s) => (
              <Select.Option key={s.shippingCode} value={s.shippingId}>
                {s.shippingName}
              </Select.Option>
            ))}
          </Select>
          <DatePicker
            style={{ flex: 1 }}
            // Wholesale: required, so highlight red until a date is chosen
            status={
              dateRequired && !formData.expectShippingTime ? 'error' : undefined
            }
            value={formData.expectShippingTime || undefined}
            placeholder={`${dateRequired ? '* ' : ''}${
              isDelivery
                ? t('order.orderDetail.expectedShippingTime')
                : t('order.orderDetail.expectedPickupTime')
            }`}
            onChange={(value) => setField('expectShippingTime', value)}
          />
        </div>
        {isDelivery && (
          <Select
            style={{ width: '100%', marginBottom: 8 }}
            allowClear
            placeholder={t('order.orderDetail.expectedShippingTime')}
            value={formData.expectShippingTimeInterval}
            getPopupContainer={(triggerNode) => triggerNode.parentNode}
            onChange={(value) => setField('expectShippingTimeInterval', value)}
          >
            {enums?.timeInterval.map((interval) => (
              <Select.Option value={interval.code} key={interval.code}>
                {interval.description}
              </Select.Option>
            ))}
          </Select>
        )}

        {/* Address */}
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography.Text strong>
            <EnvironmentOutlined /> {t('pos.deliveryTo')}
          </Typography.Text>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setEditAddress((v) => !v)}
          >
            {editAddress ? t('general.close') : t('pos.editAddress')}
          </Button>
        </div>
        {!editAddress ? (
          <Typography.Paragraph type="secondary" style={{ marginBottom: 8 }}>
            {hasAddress
              ? `${formData.consignee} ${formData.mobile || ''} · ${
                  formData.address
                }`
              : addressLoaded
              ? t('pos.missingAddress')
              : t('general.loading')}
          </Typography.Paragraph>
        ) : (
          <Space direction="vertical" style={{ width: '100%' }} size={8}>
            <UserAddressDropdown
              userId={formData.userId ? String(formData.userId) : undefined}
              onChange={(_value, option: any) => {
                if (option && option.address) applyAddress(option.address);
              }}
            />
            {addressField('consignee', t('order.orderDetail.consignee'))}
            {addressField('mobile', t('order.orderDetail.consigneeMobile'))}
            {addressField('email', t('order.orderDetail.email'))}
            {addressField('tel', t('order.orderDetail.tel'))}
            {addressField('address', t('order.orderDetail.address'))}
            {addressField('postalCode', t('order.orderDetail.postalCode'))}
            {addressField('unitNumber', t('order.orderDetail.unitNumber'))}
            {addressField('buzzerCode', t('order.orderDetail.buzzerCode'))}
            <div>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('order.orderDetail.addressType')}
              </Typography.Text>
              <Select
                style={{ width: '100%' }}
                value={formData.addressType}
                placeholder={t('order.orderDetail.addressType')}
                getPopupContainer={(triggerNode) => triggerNode.parentNode}
                onChange={(value) => setField('addressType', value)}
              >
                {enums?.addressType.map((a) => (
                  <Select.Option value={a.code} key={a.code}>
                    {a.description}
                  </Select.Option>
                ))}
              </Select>
            </div>
            {addressField('latitude', t('order.orderDetail.latitude'))}
            {addressField('longitude', t('order.orderDetail.longitude'))}
          </Space>
        )}

        {/* Remarks (same as the add-order modal's OtherInfoForm), collapsed */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: 8,
          }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Typography.Text strong>
              {t('order.add/editOrder.otherInfo')}
            </Typography.Text>
            {!showRemarks && remarkCount > 0 && (
              <Badge count={remarkCount} size="small" />
            )}
          </span>
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => setShowRemarks((v) => !v)}
          >
            {showRemarks ? t('general.close') : t('actionsColumn.edit')}
          </Button>
        </div>
        {showRemarks && (
          <Space
            direction="vertical"
            style={{ width: '100%', marginTop: 4 }}
            size={6}
          >
            {remarkField('buyerRemark', t('order.orderDetail.buyerRemark'))}
            {remarkField('sellerRemark', t('order.orderDetail.sellerRemark'))}
            {remarkField(
              'deliveryRemark',
              t('order.orderDetail.deliveryRemark')
            )}
          </Space>
        )}

        <Divider style={{ margin: '8px 0' }} />

        {/* Payment */}
        <Select
          style={{ width: '100%', marginBottom: 8 }}
          placeholder={t('order.add/editOrder.paymentMethod')}
          value={formData.payId}
          getPopupContainer={(triggerNode) => triggerNode.parentNode}
          onChange={(value, option: any) =>
            setFormData((prev) => ({
              ...prev,
              payId: value,
              payName: option && option.children,
            }))
          }
        >
          {paymentMethods.map((pm) => (
            <Select.Option key={pm.payId} value={pm.payId}>
              {pm.payName}
            </Select.Option>
          ))}
        </Select>

        {/* Pay with account balance (surplusAmount), like TotalFeeForm */}
        {userBalance > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
            <InputNumber
              style={{ flex: 1 }}
              type="number"
              min={0}
              max={userBalance}
              precision={2}
              prefix="-$"
              placeholder={t('order.orderDetail.surplusAmount')}
              value={formData.surplusAmount || undefined}
              onChange={(value: number | null) =>
                setField('surplusAmount', value || 0)
              }
            />
            <Button
              onClick={() =>
                setField(
                  'surplusAmount',
                  Math.min(
                    userBalance,
                    Number(orderAmount.totalAmount || orderAmount.orderAmount) ||
                      userBalance
                  )
                )
              }
            >
              {`${t('pos.useBalance')} ($${userBalance})`}
            </Button>
          </div>
        )}

        {/* Every non-zero line the preview returned */}
        {FEE_ROWS.filter(({ key }) => Number(orderAmount[key]) > 0).map(
          ({ key, label, prefix }) => feeRow(t(label), orderAmount[key], prefix)
        )}

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            margin: '8px 0',
          }}
        >
          <Typography.Text strong style={{ fontSize: 16 }}>
            {t('order.orderDetail.total')}
          </Typography.Text>
          <Typography.Text strong style={{ fontSize: 24, color: RED1 }}>
            {`$${
              orderAmount.orderAmount || formData.orderGoodsListTotal || '0.00'
            }`}
          </Typography.Text>
        </div>

        <Button
          type="primary"
          block
          size="large"
          loading={submitting}
          disabled={!orderGoodsList.length || !formData.payId}
          onClick={onConfirm}
        >
          {t('pos.confirmOrder')}
        </Button>
      </div>
    </>
  );
};

export default PosCart;
